// PO6 Slices 1, 2, and 4 (docs/adr/ADR-003-purchasing-and-receiving-
// surfaces.md, docs/build-briefs/PO6-S1-order-list-and-detail.md,
// docs/build-briefs/PO6-S2-cart-and-placement.md,
// docs/build-briefs/PO6-S4-receiving.md): plain-language display helpers
// for the purchase order list and detail screen, the cart/draft-proposals/
// placement screen, and the receiving screen. Kept in a separate, DOM-free
// module, the same reason ui/src/managerExceptionDisplay.ts and
// ui/src/catalogDisplay.ts are: pure functions here are unit-testable with
// a plain node:test file (test/purchasing-display.test.ts), where
// ui/src/main.ts itself cannot be imported outside a real DOM (its
// top-level mustFind calls run at import time).
//
// EVERY MONEY VALUE HERE IS A PASS-THROUGH. formatOrderEstimatedTotal
// formats exactly the estimatedTotalCents the order list/detail route (or
// placeCart's own response, same field name) returned; it never sums,
// multiplies or otherwise recomputes a total from lines. PO6-S2-F1
// (docs/backlog.md) added a per-line projection to getPurchaseOrder (id,
// product, ordered quantity, unit cost, extended cost), and the detail
// screen shows those lines too, but the ORDER'S OWN total displayed on
// screen is still order.estimatedTotalCents, the aggregate;
// test/purchasing.test.ts's own invariant test proves the two agree
// server-side, so this module has no reason to ever add the lines up
// itself and does not. The cart (Slice 2) keeps the same rule: since
// PO6-S2-F2 (docs/build-briefs/PO8-cart-total-and-minimum.md) the server
// returns a per-line estimate, per-vendor summaries and a cart estimate,
// and the helpers below only FORMAT those numbers. Nothing in this file
// multiplies a quantity by a price or adds two money values.

import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import { resolveActorDisplayName } from "./actorDisplay.ts";
import { formatCents } from "./money.ts";
import { compareNames, sortByName } from "./nameSort.ts";
import type {
  CartEstimateDto,
  CartLineDto,
  CartVendorSummaryDto,
  DraftProposalDto,
  InventoryReceiptDto,
  LineReceivingStateDto,
  OrderSubstitutionDto,
  PurchaseOrderLineDto,
  PurchaseOrderStatusChangeDto,
  PurchaseOrderSummaryDto,
} from "./api.ts";

const PURCHASE_ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: "Draft, awaiting approval",
  placed: "Placed",
  partially_received: "Partially received",
  received: "Received",
  cancelled: "Cancelled",
  exception: "Exception",
};

/** Falls back to the raw value for a status this build does not
 * recognize, rather than hiding it: an unrecognized status is a fact the
 * screen should still show, not swallow. */
export function purchaseOrderStatusLabel(status: string): string {
  return PURCHASE_ORDER_STATUS_LABELS[status] ?? status;
}

/** Straight from order.estimatedTotalCents, formatted for display. Never
 * derived from lineCount or anything else the order carries: the
 * criterion this exists to serve (P0.12's estimated-totals criterion) is
 * about showing what the route computed, not recomputing it in the
 * browser. */
export function formatOrderEstimatedTotal(
  order: Pick<PurchaseOrderSummaryDto, "estimatedTotalCents">,
): string {
  // UX105: thousands grouped ("$2,880.00"), as the cart's own totals already are; the cents are untouched.
  return formatCartMoney(order.estimatedTotalCents);
}

/** UX105: the order states a delivery can still be recorded against. A draft is awaiting approval, a received order
 * has nothing left to receive and a cancelled one never will. An exception stays receivable: resolving it can need
 * the remainder. The server refuses the rest (assertReceiptFits); this only keeps the control off the screen. */
const RECEIVABLE_ORDER_STATUSES: ReadonlySet<string> = new Set([
  "placed",
  "partially_received",
  "exception",
]);

export function purchaseOrderIsReceivable(status: string): boolean {
  return RECEIVABLE_ORDER_STATUSES.has(status);
}

/** UX105: what tells two orders from the same vendor apart on the list: when it was placed (falling back to when it
 * was created, for a draft that has not been placed) and by whom. Fixed locale, in the viewer's own time zone unless a zone
 * is passed, like the approval time. Never shows UTC (design/style-guide.md). */
export function describeOrderWhen(
  order: Pick<PurchaseOrderSummaryDto, "placedAt" | "createdAt" | "placedByDisplayName">,
  timeZone?: string,
): string {
  const when = order.placedAt ?? order.createdAt;
  const date = new Intl.DateTimeFormat("en-US", {
    ...(timeZone === undefined ? {} : { timeZone }),
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(when));
  return `${date}, ${resolveActorDisplayName(order.placedByDisplayName)}`;
}

/** MGRDAYMAJ1 (manager-day-16): what a placed order says about the vendor, in plain words. This system never emails or sends an
 * order (DEC-364), so a placed order is saved and NOT sent: the person downloads the purchase order and sends it the way the
 * vendor takes orders. A draft is still waiting for an approver and nothing has gone anywhere. `orderMethod` is the vendor's
 * own "How we order" answer, or null when none is set. */
export function placedOrderWords(
  order: Pick<PurchaseOrderSummaryDto, "vendorName" | "lineCount" | "estimatedTotalCents" | "status">,
  orderMethod: string | null,
  approverNames: readonly string[] = [],
): { title: string; detail: string } {
  const size = `${formatOrderLineCount(order)}, estimated ${formatOrderEstimatedTotal(order)}.`;
  if (order.status === "draft") {
    return {
      title: `Waiting for approval, not sent to ${order.vendorName}`,
      detail:
        `${size} Nothing has gone to ${order.vendorName}.` +
        (approverNames.length > 0 ? ` Approvers: ${approverNames.join(", ")}` : ""),
    };
  }
  const how =
    orderMethod === "email"
      ? "by email"
      : orderMethod === "website"
        ? "on their website"
        : orderMethod === "phone"
          ? "by phone"
          : orderMethod === "upload_file"
            ? "by uploading the order file on their site"
            : "yourself";
  return {
    title: `Saved for ${order.vendorName}. Not sent yet.`,
    detail: `${size} Download the purchase order and send it to ${order.vendorName} ${how}.`,
  };
}

export function formatOrderLineCount(order: Pick<PurchaseOrderSummaryDto, "lineCount">): string {
  return order.lineCount === 1 ? "1 line" : `${order.lineCount} lines`;
}

/** "2.0000" -> "2", "1.5000" -> "1.5": the database's own numeric(12,4)
 * string, trimmed to what a person actually typed as a quantity, never
 * re-parsed into a value that could round. `Number(...).toString()` is
 * exact here because the stored value already fits a double with room
 * to spare (order quantities are small integers or simple fractions). */
export function formatLineOrderedQuantity(
  line: Pick<PurchaseOrderLineDto, "orderedQuantity">,
): string {
  return Number(line.orderedQuantity).toString();
}

/** Straight from the line's own unitCostCentsAtPlacement, never
 * recomputed. Null exactly when the vendor offer this line snapshotted
 * was itself incomplete (purchase_order_lines' own CHECK), shown as
 * "Unknown" rather than a misleading $0.00. */
export function formatLineUnitCost(
  line: Pick<PurchaseOrderLineDto, "unitCostCentsAtPlacement">,
): string {
  return line.unitCostCentsAtPlacement === null
    ? "Unknown"
    : formatCents(line.unitCostCentsAtPlacement);
}

/** Straight from the line's own extendedCostCents (orderedQuantity times
 * unitCostCentsAtPlacement, computed server-side); never recomputed here
 * either, for the same reason the order-level total is not. */
export function formatLineExtendedCost(
  line: Pick<PurchaseOrderLineDto, "extendedCostCents">,
): string {
  // INVPOWALK1 (F13): thousands grouped like the order total beside it ("$3,800.00", not "$3800.00").
  return line.extendedCostCents === null ? "Unknown" : formatCartMoney(line.extendedCostCents);
}

/** order.approvedAt, straight through, formatted with a FIXED locale and
 * the viewer's own time zone (or the zone passed), never labelled UTC: PO6-S3
 * (docs/build-briefs/PO6-S3-approval.md) needs this text to match exactly
 * between test/purchasing-display.test.ts (running under Node, whatever
 * TZ the container happens to have) and e2e/po6-s3-approval.spec.ts
 * (running inside a real Chromium), so the moment shown cannot depend on
 * either environment's own clock settings. Null exactly when the order
 * has never been approved. */
export function formatPurchaseOrderApprovedAt(
  order: Pick<PurchaseOrderSummaryDto, "approvedAt">,
  timeZone?: string,
): string | null {
  if (order.approvedAt === null) {
    return null;
  }
  return new Intl.DateTimeFormat("en-US", {
    ...(timeZone === undefined ? {} : { timeZone }),
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(order.approvedAt));
}

/** The order detail screen's own "Placed by" line: the resolved display
 * name (src/domain/purchasing.ts's toOrderSummary, a LEFT JOIN on users)
 * when it resolves, ui/src/actorDisplay.ts's shared
 * FORMER_EMPLOYEE_FALLBACK otherwise -- never the raw placedByEmployeeId
 * UUID, which is what this function replaces (the bug: ui/src/main.ts
 * used to interpolate the id directly). */
export function formatPurchaseOrderPlacedBy(
  order: Pick<PurchaseOrderSummaryDto, "placedByDisplayName">,
): string {
  return `Placed by ${resolveActorDisplayName(order.placedByDisplayName)}`;
}

/** Plain-language approval state from the four fields the order carries
 * (status, approvedByEmployeeId, approvedByDisplayName, approvedAt), never
 * a role name: an order either was approved by someone, named and
 * timestamped (recorded on the order per DEC-009), is a draft still
 * awaiting that approval, or never needed one. PO6-S3: a manager reading
 * this line alone can tell whether an order is waiting, who cleared it,
 * and when, with no other field on screen required. PO6-S1-F2: the name
 * shown is the resolved approvedByDisplayName, falling back to
 * FORMER_EMPLOYEE_FALLBACK, never the raw approvedByEmployeeId id. */
export function describePurchaseOrderApproval(
  order: Pick<
    PurchaseOrderSummaryDto,
    "status" | "approvedByEmployeeId" | "approvedByDisplayName" | "approvedAt"
  >,
  timeZone?: string,
): string {
  if (order.approvedByEmployeeId !== null) {
    const at = formatPurchaseOrderApprovedAt(order, timeZone);
    const who = resolveActorDisplayName(order.approvedByDisplayName);
    return at === null ? `Approved by ${who}` : `Approved by ${who} at ${at}`;
  }
  if (order.status === "draft") {
    return "Awaiting approval";
  }
  return "No approval required";
}

export interface StatusHistoryLine {
  readonly headline: string;
  readonly reason: string | null;
  readonly exceptionReason: string | null;
  readonly resolution: string | null;
  readonly varianceLines: readonly VarianceLine[];
}

export interface VarianceLine {
  readonly productName: string;
  readonly orderedQuantity: string;
  readonly receivedQuantity: string;
  readonly outstandingQuantity: string;
}

/** The shape src/domain/receiving.ts's LineReceivingState actually has on
 * the wire (camelCase, straight JSON), read defensively: `variance` on
 * the DTO is `unknown` because it is untyped jsonb all the way from the
 * database column, and a row from before Slice 3b or from a plain
 * transition carries none at all. */
function parseVarianceLines(variance: unknown): VarianceLine[] {
  if (!Array.isArray(variance)) {
    return [];
  }
  const lines: VarianceLine[] = [];
  for (const entry of variance) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    if (
      typeof row["productName"] !== "string" ||
      typeof row["orderedQuantity"] !== "string" ||
      typeof row["receivedQuantity"] !== "string" ||
      typeof row["outstandingQuantity"] !== "string"
    ) {
      continue;
    }
    lines.push({
      productName: row["productName"],
      orderedQuantity: row["orderedQuantity"],
      receivedQuantity: row["receivedQuantity"],
      outstandingQuantity: row["outstandingQuantity"],
    });
  }
  return lines;
}

/** One status-history row, turned into plain text plus the per-line
 * variance snapshot when this row entered or resolved `exception`
 * (src/domain/purchasing.ts's own PurchaseOrderStatusChange comment: null
 * on every other row). This variance snapshot is a HISTORICAL one, as of
 * the moment the order entered or resolved exception; the order's own
 * CURRENT lines (PO6-S2-F1) are a separate, always-present field on the
 * order itself, formatted by formatLineOrderedQuantity/formatLineUnitCost/
 * formatLineExtendedCost above, not by this function. */
export function describeStatusHistoryRow(row: PurchaseOrderStatusChangeDto): StatusHistoryLine {
  const fromLabel =
    row.fromStatus === null ? "Order created" : purchaseOrderStatusLabel(row.fromStatus);
  const toLabel = purchaseOrderStatusLabel(row.toStatus);
  return {
    headline: `${fromLabel} -> ${toLabel}`,
    reason: row.reason,
    exceptionReason: row.exceptionReason,
    resolution: row.resolution,
    varianceLines: parseVarianceLines(row.variance),
  };
}

// ---------------------------------------------------------------------
// PO6 Slice 2 (docs/build-briefs/PO6-S2-cart-and-placement.md): the cart,
// draft proposals, and placement.
// ---------------------------------------------------------------------

const CART_LINE_BLOCKER_LABELS: Readonly<Record<string, string>> = {
  missing_vendor: "No vendor assigned to this product",
  vendor_inactive: "Preferred vendor is inactive",
  missing_contact: "Vendor has no contact on file",
  missing_package: "Vendor offer is missing order unit or pack size",
  missing_cost: "Vendor offer is missing a cost",
};

/** Falls back to the raw value for a blocker this build does not
 * recognize, the same "show it, don't swallow it" choice
 * purchaseOrderStatusLabel makes above. */
export function cartLineBlockerLabel(blocker: string): string {
  return CART_LINE_BLOCKER_LABELS[blocker] ?? blocker;
}

/** Null when the line carries no blocker (ready to place). Criterion 8:
 * every blocker the line has, not just the first, so a manager fixes
 * everything in one pass rather than discovering the next blocker only
 * after clearing the last one. */
export function describeCartLineBlockers(line: Pick<CartLineDto, "blockers">): string | null {
  if (line.blockers.length === 0) {
    return null;
  }
  return line.blockers.map(cartLineBlockerLabel).join("; ");
}

/** "2.0000" -> "2": the same NUMERIC(12,4)-string trim
 * formatLineOrderedQuantity uses, applied to a cart line's own `quantity`
 * field (named differently on CartLineDto than on PurchaseOrderLineDto,
 * hence a second small function rather than reusing that one). */
export function formatCartLineQuantity(line: Pick<CartLineDto, "quantity">): string {
  return Number(line.quantity).toString();
}

/** Straight from the line's own unitCostCents (the preferred vendor
 * offer's cost, unmodified); never multiplied by quantity here. The
 * extended figure comes from the server as lineEstimateCents
 * (formatCartLineTotal below), so this stays a pass-through. */
export function formatCartLineUnitCost(line: Pick<CartLineDto, "unitCostCents">): string {
  return line.unitCostCents === null ? "Unknown" : formatCents(line.unitCostCents);
}

/** One vendor's worth of cart lines, grouped for display only (no money
 * involved: grouping is by the vendorId a line already carries, never a
 * sum). `vendorKey` is "unassigned" for the one group a missing_vendor
 * line falls into (CartLineDto.vendorId is null in that case, so there is
 * no id to key on and no order this group could ever place). `ready` is
 * true only for a real vendor whose every line is blocker-free: matches
 * placeCart's own refusal rule exactly (src/domain/purchasing.ts:
 * candidates are filtered to one vendor, and a single blocked line stops
 * that vendor's whole order), so a group this function marks ready is a
 * group placeCart will actually accept. */
export interface CartVendorGroup {
  readonly vendorKey: string;
  readonly vendorId: string | null;
  readonly vendorName: string;
  readonly lines: readonly CartLineDto[];
  readonly ready: boolean;
}

export function groupCartLinesByVendor(lines: readonly CartLineDto[]): CartVendorGroup[] {
  const order: string[] = [];
  const byVendor = new Map<string, CartLineDto[]>();
  for (const line of lines) {
    const key = line.vendorId ?? "unassigned";
    const existing = byVendor.get(key);
    if (existing === undefined) {
      byVendor.set(key, [line]);
      order.push(key);
    } else {
      existing.push(line);
    }
  }
  const groups = order.map((vendorKey) => {
    const groupLines = byVendor.get(vendorKey) ?? [];
    const first = groupLines[0];
    return {
      vendorKey,
      vendorId: first?.vendorId ?? null,
      vendorName: first?.vendorName ?? "No vendor assigned",
      lines: groupLines,
      ready: vendorKey !== "unassigned" && groupLines.every((line) => line.blockers.length === 0),
    };
  });
  // UX88 (DEC-339): vendors read A to Z; the "No vendor assigned" bucket,
  // which needs attention before anything can be ordered, stays first.
  return sortByName(groups, (group) => group.vendorName, {
    pinned: (group) => group.vendorKey === "unassigned",
  });
}

const PROPOSAL_SOURCE_LABELS: Readonly<Record<string, string>> = {
  below_par: "Below par",
  low_stock: "Low stock",
  out_of_stock: "Out of stock",
};

export function draftProposalSourceLabel(source: string): string {
  return PROPOSAL_SOURCE_LABELS[source] ?? source;
}

/** UIFIX5: the one status a row shows as its chip, the most urgent of its reasons (out of stock, then low stock, then
 * below par). Every reason is still in describeDraftProposalSources. */
export function primaryDraftProposalSourceLabel(sources: readonly string[]): string {
  for (const source of ["out_of_stock", "low_stock", "below_par"]) {
    if (sources.includes(source)) return draftProposalSourceLabel(source);
  }
  return sources.length === 0 ? "" : draftProposalSourceLabel(sources[0]!);
}

/** LOCFLOOR1 (sweep B4): the one status chip of a proposal row. A row exactly at par is listed with nothing to order
 * (DEC-176 lists at or below par), so its chip says At par; "Low" is only for stock below par, which keeps the chip
 * from contradicting the disabled "At par" button beside it. Every other row reads as primaryDraftProposalSourceLabel. */
export function proposalStatusChipLabel(
  proposal: Pick<DraftProposalDto, "sources" | "suggestedQuantity">,
): string {
  return proposalShortfallIsZero(proposal)
    ? "At par"
    : primaryDraftProposalSourceLabel(proposal.sources);
}

/** Every reason a proposal exists, not just the first (a product can be
 * out of stock AND below par at once), mirroring describeCartLineBlockers
 * above. */
export function describeDraftProposalSources(sources: readonly string[]): string {
  return sources.map(draftProposalSourceLabel).join(", ");
}

export function formatProposalOnHand(proposal: Pick<DraftProposalDto, "onHand">): string {
  return Number(proposal.onHand).toString();
}

// UX16-F2 (docs/build-briefs/UX16-F2-negative-on-hand-shown-not-hidden.md;
// DEC-178). Plain-language warning shown beside a negative on-hand figure.
// Null when on-hand is zero or positive: nothing renders, matching this
// file's own "-" convention for an absent fact rather than inventing a
// hidden default state.
export function formatProposalOnHandWarning(
  proposal: Pick<DraftProposalDto, "onHand">,
): string | null {
  if (Number(proposal.onHand) >= 0) return null;
  return "On hand is negative. The next count corrects it.";
}

/** UX16 (DEC-058), criterion 1: "the Par levels for each" -- the number a
 * suggestion is derived FROM, shown next to on-hand rather than implied.
 * "N/A" when the product carries no par at all, the same "show it, don't
 * invent it" choice formatProposalSuggestedQuantity makes below; a
 * product with no par has no target, and that absence is itself the
 * fact this function shows. */
export function formatProposalParLevel(proposal: Pick<DraftProposalDto, "parLevel">): string {
  return proposal.parLevel === null ? "-" : Number(proposal.parLevel).toString();
}

/** "-" when the product has no par (generateDraftProposals returns null
 * rather than inventing a target to close a shortfall against); otherwise
 * par minus on-hand, exactly as the route computed it. */
export function formatProposalSuggestedQuantity(
  proposal: Pick<DraftProposalDto, "suggestedQuantity">,
): string {
  return proposal.suggestedQuantity === null ? "-" : Number(proposal.suggestedQuantity).toString();
}

// UX105: one place that words a quantity of a counted thing. The unit word is singular at exactly 1 and plural
// for everything else (0, 0.5, 4), never "bottle(s)".
const COUNT_UNIT_WORDS: Readonly<Record<string, readonly [string, string]>> = {
  bottle: ["bottle", "bottles"],
  can: ["can", "cans"],
  keg: ["keg", "kegs"],
  unit: ["unit", "units"],
  case: ["case", "cases"],
};

/** A quantity to at most two decimals, trailing zeros trimmed ("4.0591" -> "4.06", "2.5000" -> "2.5"), and "0" for
 * a value that rounds to zero without being negative. Display only: nothing is computed from the result. */
export function trimQuantity(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

/** "1 bottle", "3 bottles", "0.5 bottles", "-0.06 bottles". A unit this build has no words for is shown as given. */
export function formatCountQuantity(value: number | string, unit: string): string {
  const number = Number(value);
  const words = COUNT_UNIT_WORDS[unit.toLowerCase()];
  const shown = trimQuantity(number);
  if (words === undefined) return `${shown} ${unit}`;
  return `${shown} ${Math.abs(number) === 1 ? words[0] : words[1]}`;
}

/** The suggested quantity of a proposal, in its count unit when one resolves. A whole counted unit (a bottle, a can,
 * a keg or a unit) is rounded up to whole units: you cannot order 4.0591 bottles, and 5 is what "Add to cart" stages
 * (roundShortfallUpToWholeUnit). a dash with no par. */
export function formatProposalSuggested(
  proposal: Pick<DraftProposalDto, "suggestedQuantity" | "countUnit">,
): string {
  if (proposal.suggestedQuantity === null) return "-";
  const unit = proposal.countUnit ?? null;
  if (unit === null || COUNT_UNIT_WORDS[unit] === undefined) {
    return trimQuantity(Number(proposal.suggestedQuantity));
  }
  return formatCountQuantity(Math.ceil(Number(proposal.suggestedQuantity) - 1e-9), unit);
}

/** UX16 (DEC-058), criterion 2: "a button to just add every bottle to a
 * new order that is below par", staging each at its OWN computed
 * shortfall (par minus on-hand). Every field here is a pass-through of a
 * proposal already returned by GET .../purchasing/proposals; nothing is
 * typed and nothing is guessed (CLAUDE.md's money rule; DEC-035).
 *
 * Filters on `sources.includes("below_par")`, the exact flag
 * src/domain/purchasing.ts's sourcesOf sets, and that function only ever
 * sets it when parLevel is not null (it reads
 * `row.parLevel !== null && onHand < parLevel` before pushing the
 * source). So every proposal this filter keeps already carries a
 * non-null suggestedQuantity by construction; the null check below is
 * defensive, not a case the domain is expected to reach, and a proposal
 * with no par (out-of-stock or low-stock alone, sources never carrying
 * "below_par") is excluded here exactly as DEC-058 requires -- never
 * included with a guessed quantity. */
export interface BulkBelowParStageLine {
  readonly liquorProductId: string;
  readonly productName: string;
  readonly quantity: string;
}

/** UX66 (owner report 2026-09-29): "it should round to the nearest whole
 * unit for that inventory item. Using decimal places for ordering makes
 * zero sense." A cart line orders whole bottles, cans, kegs or units,
 * never a fraction of one, so par-minus-on-hand's own decimal shortfall
 * (an oz-based on-hand figure divided by container size, e.g. 1.473) is
 * rounded to the nearest whole number before it is ever staged. Rounding
 * can only take a positive shortfall down to exactly zero when it is
 * already below 0.5; this function is never called with a shortfall
 * that is zero or negative to begin with (both belowParProposalsToStage
 * and handleAddProposalToCart in ui/src/main.ts guard that before
 * calling it), so the minimum-of-1 floor below applies to every input
 * this function actually receives, never silently masking a real zero.
 * Never returns "0": staging zero would be the exact "dead-end" bulk-add
 * failure UX65 already fixed for an exactly-at-par row, and a rounded-
 * down shortfall must not reopen it. */
export function roundQuantityToWholeUnit(quantity: string): string {
  const numeric = Number(quantity);
  const rounded = Math.round(numeric);
  return String(rounded > 0 ? rounded : 1);
}

/** UX80: a par SHORTFALL is rounded UP to a whole unit, never to the nearest,
 * so a cart line never stages less than the item is short (1.4 short stages 2,
 * not 1) and never stages a fraction of a bottle. Typed quantities keep UX66's
 * nearest-whole rule above. PO9 (TF4-F3): this is now only the fallback for a
 * proposal with no preferred offer; the count-unit to order-unit (case)
 * conversion is done server-side and read through stagedQuantityForProposal.
 * Never returns "0". */
export function roundShortfallUpToWholeUnit(quantity: string): string {
  const rounded = Math.ceil(Number(quantity) - 1e-9);
  return String(rounded > 0 ? rounded : 1);
}

/** PO9 (TF4-F3): the quantity a proposal stages into the cart, which is in the
 * preferred offer's ORDER unit. The server converts the bottle shortfall into
 * whole order units (rounded up); this only reads that figure. With no
 * preferred offer at all there is nothing to convert and the cart line is
 * blocked from placement anyway, so the old whole-unit shortfall is staged.
 * Null when an offer exists but the shortfall cannot be converted (pack size
 * unknown, or counted in ounces): staging the bottle figure there would be
 * read as cases later. */
export function stagedQuantityForProposal(
  proposal: Pick<DraftProposalDto, "suggestedQuantity" | "order" | "orderBlockedReason">,
): string | null {
  if (proposal.order != null) {
    return proposal.order.orderQuantity;
  }
  if (proposal.orderBlockedReason != null) {
    return null;
  }
  return roundShortfallUpToWholeUnit(proposal.suggestedQuantity ?? "1");
}

/** UX105: "Order 1 case (12 bottles)" next to a proposal's staged quantity, "Order 3 bottles" when the order unit is
 * the bottle itself (nothing to restate), or the plain reason it cannot be converted, or null with no preferred
 * offer. The unit word agrees with the number. */
export function formatProposalOrderCoverage(
  proposal: Pick<DraftProposalDto, "order" | "orderBlockedReason">,
): string | null {
  if (proposal.order != null) {
    const unit = proposal.order.orderUnit.toLowerCase();
    const ordered = formatCountQuantity(proposal.order.orderQuantity, unit);
    if (
      unit === "bottle" ||
      proposal.order.bottlesCovered === Number(proposal.order.orderQuantity)
    ) {
      return `Order ${ordered}`;
    }
    return `Order ${ordered} (${formatCountQuantity(proposal.order.bottlesCovered, "bottle")})`;
  }
  return proposal.orderBlockedReason ?? null;
}

/** LOCORDER1 (B7): who a proposal would be ordered from and what it costs, on the proposal row. The cost is the
 * preferred offer's per order unit, with the line estimate beside it (quantity x cost, whole cents). A missing cost
 * is said in words, never shown as $0.00 (design-language rule 14). Null with no preferred offer. */
export function formatProposalVendorCost(
  proposal: Pick<DraftProposalDto, "order">,
): string | null {
  const order = proposal.order;
  if (order == null) return null;
  const vendor = order.vendorName ?? "Vendor not set";
  const cost = order.unitCostCents;
  if (cost === null || cost === undefined) return `${vendor}, cost not set`;
  const quantity = Number(order.orderQuantity);
  const unit = order.orderUnit.toLowerCase();
  const each = `${formatCartMoney(cost)} per ${unit}`;
  if (!Number.isFinite(quantity) || quantity <= 0) return `${vendor}, ${each}`;
  return `${vendor}, ${each}, about ${formatCartMoney(Math.round(quantity * cost))}`;
}

// Order units that are themselves one counted thing, where "bottles" would be
// the wrong word (a keg, a can, a generic unit). Bottle counts are shown for
// every other order unit.
const NON_BOTTLE_SINGLE_ORDER_UNITS: ReadonlySet<string> = new Set(["keg", "can", "unit"]);

/** TF4-F3: the bottle count beside an order unit, "Case, 36 bottles". Null
 * for a keg, can or unit offer (no bottle claim to make). "bottles per case
 * not set" when the pack is unknown, never a guessed number. */
export function formatOrderUnitBottles(line: {
  orderUnit: string | null | undefined;
  bottlesPerOrderUnit: string | null | undefined;
  bottleCount: number | null | undefined;
}): string | null {
  const unitWord = line.orderUnit == null ? "Unit" : capitalizeFirst(line.orderUnit.toLowerCase());
  if (line.orderUnit != null && NON_BOTTLE_SINGLE_ORDER_UNITS.has(line.orderUnit.trim().toLowerCase())) {
    return null;
  }
  if (line.bottleCount == null) {
    if (line.bottlesPerOrderUnit == null) {
      return `${unitWord}, bottles per ${line.orderUnit == null ? "unit" : line.orderUnit.toLowerCase()} not set`;
    }
    return `${unitWord}, not a whole number of bottles`;
  }
  return `${unitWord}, ${line.bottleCount} ${line.bottleCount === 1 ? "bottle" : "bottles"}`;
}

/** KEGRECV1: what an order line shows for its unit: "Case, 36 bottles" for a pack of bottles, or just the unit word
 * ("Keg", "Can") where there is no bottle count to state. Null when the line names no unit. */
export function formatOrderUnitLabel(line: {
  orderUnit: string | null | undefined;
  bottlesPerOrderUnit: string | null | undefined;
  bottleCount: number | null | undefined;
}): string | null {
  const bottles = formatOrderUnitBottles(line);
  if (bottles !== null) {
    return bottles;
  }
  return line.orderUnit == null || line.orderUnit.trim() === "" ? null : capitalizeFirst(line.orderUnit.trim().toLowerCase());
}

export function belowParProposalsToStage(
  proposals: readonly Pick<
    DraftProposalDto,
    "liquorProductId" | "productName" | "sources" | "suggestedQuantity" | "order" | "orderBlockedReason"
  >[],
): BulkBelowParStageLine[] {
  const lines: BulkBelowParStageLine[] = [];
  for (const proposal of proposals) {
    if (!proposal.sources.includes("below_par")) {
      continue;
    }
    if (proposal.suggestedQuantity === null) {
      continue;
    }
    // INV8 (DEC-176), backend record amendment 2026-09-23: the Low list's
    // own boundary is now at-or-below par, so an exactly-at-par row's own
    // suggestedQuantity is "0.0000" rather than excluded upstream.
    // src/domain/purchasing.ts's setCartLine calls requirePositiveQuantity,
    // which refuses exactly zero (proven directly against the real domain
    // function in the backend record); staging a zero-quantity line here
    // would fail that one PUT silently inside this bulk loop's own
    // per-line try, without a targeted message. Skipped here instead, the
    // same "not fixed in the backend record, a UI decision" gap that
    // record names explicitly.
    if (Number(proposal.suggestedQuantity) === 0) {
      continue;
    }
    // PO9: stage in the order unit, never the bottle shortfall.
    const quantity = stagedQuantityForProposal(proposal);
    if (quantity === null) {
      continue;
    }
    lines.push({
      liquorProductId: proposal.liquorProductId,
      productName: proposal.productName,
      quantity,
    });
  }
  return lines;
}

/** UX65: "Add all below par to cart" disables silently whenever
 * belowParProposalsToStage returns nothing, with no reason shown next to
 * it (the owner's own report: "it refuses to let me press the button").
 * This computes the plain-language reason from the same loaded proposals
 * the button's own enabled check reads -- never re-fetched, never
 * guessed -- or null when the button is enabled (or the list is empty,
 * which the screen's own empty-state message already covers). Two
 * distinguishable causes, because they call for different owner action:
 * no product on the list has a par level set at all (set one on
 * Inventory), versus some products have a par level but no bottle or keg
 * size to compare it against (set a container size). A third, rarer
 * cause -- every below_par product already sits exactly at its own par,
 * shortfall zero -- gets its own message rather than falling through to
 * one of the other two dishonestly. */
export function belowParButtonDisabledReason(
  proposals: readonly Pick<
    DraftProposalDto,
    | "liquorProductId"
    | "productName"
    | "sources"
    | "suggestedQuantity"
    | "parLevel"
    | "countUnit"
    | "onHandInCountUnit"
    | "order"
    | "orderBlockedReason"
  >[],
): string | null {
  if (proposals.length === 0) return null;
  if (belowParProposalsToStage(proposals).length > 0) return null;

  // PO9: every below-par product has a shortfall but none can be converted
  // into an order quantity; say the real reason rather than "nothing to add".
  const unconvertible = proposals.find(
    (proposal) => proposal.orderBlockedReason != null && Number(proposal.suggestedQuantity) > 0,
  );
  if (unconvertible !== undefined) {
    return unconvertible.orderBlockedReason ?? null;
  }

  const withPar = proposals.filter((proposal) => proposal.parLevel !== null);
  if (withPar.length === 0) {
    return "No product on this list has a par level set yet. Set par on the Inventory screen.";
  }

  const missingUnit = withPar.filter(
    (proposal) => proposal.countUnit == null || proposal.onHandInCountUnit == null,
  );
  if (missingUnit.length > 0) {
    const n = missingUnit.length;
    const isAre = n === 1 ? "is" : "are";
    const hasHave = n === 1 ? "has" : "have";
    const plural = n === 1 ? "" : "s";
    return `${n} product${plural} on this list ${isAre} below par but ${hasHave} no bottle or keg size set, so no order quantity can be worked out.`;
  }

  return "Every product below par already has enough on hand for its par level, so there is nothing to add.";
}

// ---------------------------------------------------------------------
// INV8 (docs/build-briefs/INV8-par-in-containers.md, DEC-176). Par
// compared in the item's own count unit, surfaced on the Low list
// (Section 2.5) and, client-side only, as the item editor's own read-only
// unit hint (Section 2.6).
// ---------------------------------------------------------------------

export const COUNT_UNIT_LABELS: Readonly<Record<string, string>> = {
  bottle: "bottles",
  can: "cans",
  keg: "kegs",
  unit: "units",
};

/** Falls back to the raw value for a count unit this build does not
 * recognize, the same "show it, don't swallow it" choice
 * purchaseOrderStatusLabel/cartLineBlockerLabel/draftProposalSourceLabel
 * all make above. Null in, null out: "no resolvable count unit" is a
 * distinct case from "an unrecognized one," handled by the caller. */
export function countUnitLabel(countUnit: string | null | undefined): string | null {
  if (countUnit === null || countUnit === undefined) {
    return null;
  }
  return COUNT_UNIT_LABELS[countUnit] ?? countUnit;
}

/** Section 2.5's "(set a container size or keg size to compare against
 * par)" hint text, verbatim, shown on a below_par-eligible row whose
 * countUnit is null rather than silently implying a false confidence. */
export const NO_COUNT_UNIT_HINT = "(set a container size or keg size to compare against par)";

export interface ProposalUnitDisplay {
  readonly onHandText: string;
  readonly parText: string;
  readonly hint: string | null;
}

/** One proposal row's "On hand"/"Par" text and (when applicable) the
 * no-resolvable-unit hint, in one call so a caller never has to
 * re-derive "is this row below_par and unit-resolved" independently.
 * Two cases (Section 2.5):
 *  - below_par, unit resolved: onHandInCountUnit + parLevel, both with
 *    the resolved unit label, no hint.
 *  - everything else: the raw, unlabeled onHand/parLevel figures
 *    (formatProposalOnHand/formatProposalParLevel, unchanged) -- those
 *    never had a cross-unit problem (Section 2.5's own reasoning) --
 *    plus the hint whenever this product carries a par level that could
 *    not actually be compared (parLevel non-null, countUnit null). A
 *    product can reach the Low list via low_stock/out_of_stock alone
 *    (sourcesOf, src/domain/purchasing.ts) with a par level set but an
 *    unresolved count unit: `sourcesOf` only ever pushes "below_par" for
 *    a row whose countUnit already resolved (src/domain/purchasing.ts's
 *    own Section 2.4 comparison), so a below_par row with a null
 *    countUnit cannot occur; the hint is keyed on parLevel/countUnit
 *    directly rather than on the below_par source for exactly that
 *    reason, so this real, reachable combination still gets it. */
export function formatProposalUnitDisplay(
  proposal: Pick<
    DraftProposalDto,
    "onHand" | "parLevel" | "sources" | "countUnit" | "onHandInCountUnit"
  >,
): ProposalUnitDisplay {
  const isBelowPar = proposal.sources.includes("below_par");
  const label = countUnitLabel(proposal.countUnit);
  // sourcesOf (src/domain/purchasing.ts) only ever pushes "below_par" when
  // parLevel is not null (belowParProposalsToStage's own comment already
  // states this), so parLevel is guaranteed non-null whenever isBelowPar
  // is true; the explicit check below is the same defensive-not-assumed
  // discipline this codebase already applies elsewhere, and avoids
  // reaching for formatProposalParLevel's own null placeholder text here.
  if (
    isBelowPar &&
    label !== null &&
    proposal.onHandInCountUnit != null &&
    proposal.parLevel !== null
  ) {
    return {
      onHandText: formatCountQuantity(proposal.onHandInCountUnit, proposal.countUnit ?? label),
      parText: formatCountQuantity(proposal.parLevel, proposal.countUnit ?? label),
      hint: null,
    };
  }
  return {
    onHandText: formatProposalOnHand(proposal),
    // UX105: to the unit's precision, with the unit word agreeing with the number.
    parText: formatProposalParLevel(proposal),
    hint:
      proposal.parLevel !== null &&
      (proposal.countUnit === null || proposal.countUnit === undefined)
        ? NO_COUNT_UNIT_HINT
        : null,
  };
}

/** A below_par-sourced row whose own suggestedQuantity is exactly zero
 * (an at-or-below-par boundary that landed exactly AT par, PRD P0.12/
 * DEC-176's own inclusive boundary): setCartLine's own
 * requirePositiveQuantity refuses exactly zero, so "Add to cart" for
 * this row must be disabled rather than offered and failing. */
export function proposalShortfallIsZero(
  proposal: Pick<DraftProposalDto, "sources" | "suggestedQuantity">,
): boolean {
  return (
    proposal.sources.includes("below_par") &&
    proposal.suggestedQuantity !== null &&
    Number(proposal.suggestedQuantity) === 0
  );
}

/** Section 2.1's own count-unit derivation (src/domain/
 * inventoryStockState.ts's resolveCountUnit), reproduced client-side for
 * the item editor's own read-only Par-level hint (Section 2.6), which
 * needs the label to update live as a person edits the form, before any
 * save round-trips through the server. Mirrors that function's four
 * cases exactly: whole-unit sale mechanism -> "unit"; per-ounce + keg
 * container -> "keg" (only once a keg size is actually picked; DEC-176's
 * own "never typed" rule means an unset keg size is unresolved, never a
 * silent fallback); per-ounce + bottle/can container -> the container
 * type itself; anything else -> unresolved. Never a fallback to a raw
 * ledger unit, the same rule Section 2.1 states twice for the backend
 * version. */
export function resolveClientCountUnit(input: {
  readonly saleMechanismIsWholeUnit: boolean;
  readonly containerType: string | null;
  readonly hasDefaultKegSize: boolean;
}): "unit" | "keg" | "bottle" | "can" | null {
  if (input.saleMechanismIsWholeUnit) {
    return "unit";
  }
  if (input.containerType === "keg") {
    return input.hasDefaultKegSize ? "keg" : null;
  }
  if (input.containerType === "bottle" || input.containerType === "can") {
    return input.containerType;
  }
  return null;
}

/** "Par is in bottles (750 ml)" / "Par is in kegs (half barrel)" / "Par
 * is in units" -- the item editor's own read-only hint next to Par level
 * (Section 2.6). Purely informative text, never a picker: the typed par
 * VALUE is unchanged, only its label. Null when no count unit resolves
 * (Section 2.1 case 4, an unset keg size, or a bottle/can with no size
 * recorded yet), so the caller can hide the hint entirely rather than
 * show a label for a comparison that cannot actually be made. */
export function describeParUnitHint(input: {
  readonly saleMechanismIsWholeUnit: boolean;
  readonly containerType: string | null;
  readonly sizeValue: string | null;
  readonly sizeUnit: string | null;
  readonly defaultKegSizeLabel: string | null;
}): string | null {
  const unit = resolveClientCountUnit({
    saleMechanismIsWholeUnit: input.saleMechanismIsWholeUnit,
    containerType: input.containerType,
    hasDefaultKegSize: input.defaultKegSizeLabel !== null,
  });
  if (unit === null) {
    return null;
  }
  if (unit === "unit") {
    return "Par is in units";
  }
  if (unit === "keg") {
    // resolveClientCountUnit only ever returns "keg" when
    // hasDefaultKegSize was true, so defaultKegSizeLabel is non-null here
    // by construction; the fallback below is defensive, never expected to
    // run.
    return `Par is in kegs (${(input.defaultKegSizeLabel ?? "").toLowerCase()})`;
  }
  const plural = unit === "bottle" ? "bottles" : "cans";
  if (input.sizeValue !== null && input.sizeUnit !== null) {
    // UX105: a litre is "L" (a lowercase l next to a 1 reads as 11); millilitres and ounces keep their symbols.
    const sizeUnit = input.sizeUnit.toLowerCase() === "l" ? "L" : input.sizeUnit;
    return `Par is in ${plural} (${input.sizeValue} ${sizeUnit})`;
  }
  return `Par is in ${plural}`;
}

/** Compares two NUMERIC(12,4)-shaped decimal strings (this codebase's own
 * "X.0000" convention) without ever going through a JS float, mirroring
 * this repository's established no-floats discipline for quantities
 * (src/domain/inventoryQuantity.ts's own scaled-BigInt arithmetic) even
 * though this particular comparison never leaves the browser and is used
 * for display order only, never money. Returns a negative number, zero,
 * or a positive number the way Array.prototype.sort expects; null sorts
 * LAST regardless of comparison direction (a proposal with no par has no
 * shortfall to rank by). */
function compareQuantityStrings(a: string | null, b: string | null): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  const scale = (value: string): bigint => {
    const negative = value.startsWith("-");
    const unsigned = negative ? value.slice(1) : value;
    const [whole, fractionRaw] = unsigned.split(".");
    const fraction = (fractionRaw ?? "").padEnd(4, "0").slice(0, 4);
    const magnitude = BigInt(whole && whole.length > 0 ? whole : "0") * 10000n + BigInt(fraction);
    return negative ? -magnitude : magnitude;
  };
  const diff = scale(a) - scale(b);
  return diff < 0n ? -1 : diff > 0n ? 1 : 0;
}

export type ProposalSortOrder = "shortfall" | "name";

/** Section 2.5's sort control: client-side only, no new network call, on
 * the already-fetched proposals list. "Largest shortfall first" (the
 * default) compares suggestedQuantity DESCENDING, null sorted last
 * regardless (false green 7's own required control: a scrambled seed
 * order must actually change on render, not merely happen to already be
 * sorted). "Product name" is a plain locale-aware ascending compare.
 * Returns a NEW array; never mutates its input, so the caller's own
 * state.purchasingCartProposals stays the single record of what was
 * actually fetched from the server. */
export function sortProposalsForDisplay<
  T extends Pick<DraftProposalDto, "productName" | "suggestedQuantity">,
>(proposals: readonly T[], order: ProposalSortOrder): T[] {
  const copy = [...proposals];
  if (order === "name") {
    copy.sort((a, b) => compareNames(a.productName, b.productName));
    return copy;
  }
  // UX65: this used to call compareQuantityStrings(b.suggestedQuantity,
  // a.suggestedQuantity) directly, swapping the arguments to turn the
  // ascending helper into a descending one. compareQuantityStrings's own
  // null-last guarantee only holds for a direct, un-swapped call; under
  // the swap, a proposal with no shortfall to show (no par, or par set
  // but no resolvable container/keg size) sorted to the FRONT instead of
  // the back, burying the real shortfalls this sort exists to surface
  // (the owner's own report: three no-par products at the top of
  // "Largest shortfall first"). Null is placed last explicitly, ahead of
  // the numeric compare, so the guarantee holds regardless of which side
  // of the comparator either value lands on.
  copy.sort((a, b) => {
    if (a.suggestedQuantity === null && b.suggestedQuantity === null) return 0;
    if (a.suggestedQuantity === null) return 1;
    if (b.suggestedQuantity === null) return -1;
    return compareQuantityStrings(b.suggestedQuantity, a.suggestedQuantity);
  });
  return copy;
}

// ---------------------------------------------------------------------
// PO6 Slice 4 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md;
// docs/build-briefs/PO6-S4-receiving.md). The receiving screen: what one
// order's own lines still owe, and the fixed unit vocabulary a receipt
// against a line picks from.
// ---------------------------------------------------------------------

/** "6.0000" -> "6", the same NUMERIC(12,4)-string trim
 * formatLineOrderedQuantity/formatCartLineQuantity use above, applied to
 * any of LineReceivingStateDto's three quantity fields. One function
 * rather than three near-identical copies: the trim itself does not
 * depend on which field is being trimmed, only the caller does. */
export function formatReceivingQuantity(value: string): string {
  return Number(value).toString();
}

// PO13 (docs/build-briefs/PO13-receive-substitution-ui.md). A delivery of a different product than the one ordered.

/** The mark on an order that has at least one substitution. */
export const ORDER_SUBSTITUTION_LABEL = "Includes a substitution";

/** The label on the receiving line's control. */
export const SUBSTITUTE_CONTROL_LABEL = "They sent something else";

/** The substitutions that belong to one order line, in the order they were received. */
export function substitutionsForLine(
  substitutions: readonly OrderSubstitutionDto[],
  purchaseOrderLineId: string,
): OrderSubstitutionDto[] {
  return substitutions.filter((sub) => sub.purchaseOrderLineId === purchaseOrderLineId);
}

/** "Sent instead: Rye 100, 2 bottle". The quantity is the receipt's own, trimmed of its trailing zeros. */
export function describeSubstitution(sub: OrderSubstitutionDto): string {
  return `Sent instead: ${sub.deliveredProductName}, ${formatReceivingQuantity(sub.quantity)} ${sub.unit}`;
}

// PO7-S4-F3 (docs/build-briefs/PO7-S4-F3-case-line-receiving.md). A line bought
// by the case is received in cases, and the screen says both readings.

/** The chip value for "received in the line's own order unit". */
export const ORDER_UNIT_RECEIVING_UNIT_VALUE = "order_unit";

type PackedLine = Pick<LineReceivingStateDto, "orderUnit" | "bottlesPerOrderUnit">;

/** True when the line's order unit holds more than one bottle, so its quantities
 * are in cases (or boxes) and not in bottles. */
export function receivingLineIsPacked(line: PackedLine): boolean {
  const pack = line.bottlesPerOrderUnit;
  return pack !== undefined && pack !== null && Number(pack) > 1;
}

/** "case" and 2 -> "cases"; 1 -> "case". */
export function orderUnitWord(orderUnit: string | null | undefined, quantity: number): string {
  const word = (orderUnit ?? "unit").trim().toLowerCase() || "unit";
  if (quantity === 1) {
    return word;
  }
  return /(s|x|z|ch|sh)$/.test(word) ? `${word}es` : `${word}s`;
}

/** "2 cases (24 bottles)" for a packed line, or the bare number for any other.
 * `quantityText` is what was typed or stored; a quantity that is not a positive
 * number is returned as it is. */
export function describeOrderUnitQuantity(line: PackedLine, quantityText: string): string {
  const quantity = Number(quantityText);
  if (!receivingLineIsPacked(line) || quantityText.trim() === "" || !Number.isFinite(quantity)) {
    return quantityText;
  }
  if (quantity === 0) {
    // A zero reads with its unit ("0 cases"), never as a bare "0".
    return `0 ${orderUnitWord(line.orderUnit, 0)}`;
  }
  if (quantity < 0) {
    return quantityText;
  }
  const bottles = quantity * Number(line.bottlesPerOrderUnit);
  const shown = Number(quantity.toFixed(4)).toString();
  const bottleText = Number.isInteger(bottles)
    ? `${bottles} ${bottles === 1 ? "bottle" : "bottles"}`
    : `${Number(bottles.toFixed(4))} bottles, not a whole number`;
  return `${shown} ${orderUnitWord(line.orderUnit, quantity)} (${bottleText})`;
}

/** The one chip a packed line offers: its own order unit, with the pack stated. */
export function orderUnitReceivingOption(line: PackedLine): ReceivingUnitOption {
  const pack = Number(line.bottlesPerOrderUnit);
  const word = orderUnitWord(line.orderUnit, 2);
  return {
    value: ORDER_UNIT_RECEIVING_UNIT_VALUE,
    label: `${word.charAt(0).toUpperCase()}${word.slice(1)} (${pack} bottles each)`,
  };
}

/** True once a line's own outstanding quantity is exactly zero: nothing
 * further to record against it. Named rather than an inline `=== "0"`
 * comparison at each call site, since the stored value is a string like
 * "0.0000", not the bare digit. */
export function receivingLineIsClosed(
  line: Pick<LineReceivingStateDto, "outstandingQuantity">,
): boolean {
  return Number(line.outstandingQuantity) === 0;
}

/** Which unit chips the receiving screen offers for one product, keyed on
 * that product's own item_kind (read from GET .../liquor-products/:id,
 * the only route this screen can reach a unit vocabulary from at all:
 * neither getOrderReceivingView above nor getPurchaseOrder's own line
 * projection (PO6-S2-F1) returns the order's own placement unit --
 * findings recorded in docs/build-briefs/PO6-S4-receiving.md. Mirrors
 * ui/src/admin.ts's private countUnitOptionsForItemKind exactly
 * (liquor/beer/wine share one oz/bottle vocabulary, a whole-unit kind gets
 * its own disjoint "units" set); kept as its own small copy rather than a
 * shared export, the same "no single source for a four-line helper"
 * precedent admin.ts's own renderChipRadioGroup comment already
 * established for this codebase.
 *
 * ITEM2 Slice 5 (docs/build-briefs/ITEM2-a-kind-sale-mechanism-is-picked-
 * in-settings.md Section 4's "Slice 5" paragraph (d)): took an item_kind
 * literal ("packaged" versus everything else) until this item; a
 * tenant-added whole-unit kind's own item_kind (its own value, never
 * "packaged") could never match. Generalized to the already-fetched
 * PRODUCT's own `unit_price_cents` (non-null exactly on a whole-unit
 * product, db/schema.ts's liquor_products_kind_fields_check trigger),
 * the identical derivation ui/src/main.ts's tilePriceLabelForNonCocktailItem
 * uses, rather than a fresh sale_mechanism lookup this screen has no
 * cheap access to. */
export interface ReceivingUnitOption {
  readonly value: string;
  readonly label: string;
}
const LIQUOR_RECEIVING_UNITS: readonly ReceivingUnitOption[] = [
  { value: "oz", label: "Ounces" },
  { value: "bottle", label: "Bottles" },
];
const KEG_NO_SIZE_RECEIVING_UNITS: readonly ReceivingUnitOption[] = [{ value: "oz", label: "Ounces" }];
const PACKAGED_RECEIVING_UNITS: readonly ReceivingUnitOption[] = [
  { value: "unit", label: "Units" },
];
// INV14 (docs/build-briefs/INV14-container-volume-model.md, DEC-121).
// src/domain/inventory.ts's recordStockReceipt now accepts a container
// count (receivedContainerKegSize + receivedContainerCount) as an
// alternative to a raw quantity/unit pair, and resolves it to ounces
// before the write path runs. Left naming the same oz/bottle pair every
// other per-ounce kind uses for the KEG/draft-beer case: swapping beer's
// "Bottles" chip for a keg-size-and-count entry mode is its own separate,
// still-untouched follow-up (INV8-F1's own "What this design does NOT
// cover"). A draft-beer product still received as "1 Bottle" reports
// itself honestly as a genuine second, non-oz bucket (mixedUnits: true,
// excluded from belowPar) rather than silently reading a nonsense
// negative number, so this screen not yet offering the new path for kegs
// is safe, not silently wrong.
//
// INV8-F1 Section 5: a `bottle`/`can`-configured product now offers its
// own real container chip instead, resolved below.
export interface ReceivingProductContainerInfo {
  readonly containerType: string | null;
  readonly sizeValue: string | null;
  readonly sizeUnit: string | null;
  /** KEGRECV1: the keg size a keg product is set to; a keg with none cannot be counted in kegs. */
  readonly defaultKegSize?: string | null;
}

/** The one new chip value this item adds, submitted in place of a raw
 * `unit` string exactly when the product's own container was picked
 * (never confused with the pre-existing, mismatched-workaround raw
 * `"bottle"` chip a keg product still offers). */
export const CONTAINER_RECEIVING_UNIT_VALUE = "container";

const CONTAINER_TYPE_CHIP_LABELS: Readonly<Record<string, string>> = {
  bottle: "Bottles",
  can: "Cans",
  keg: "Kegs",
};

/** Mirrors src/domain/inventoryStockState.ts's own convertSizeToOunces
 * sizeUnit vocabulary (oz/ml/l) just closely enough to decide whether a
 * size RESOLVES at all, never to compute the ounces themselves: the real
 * conversion happens server-side, at receipt time, from these same
 * already-fetched values, which this screen only ever submits raw
 * (receivedContainerCount, never a client-computed ounce figure). */
function containerSizeResolves(sizeValue: string | null, sizeUnit: string | null): boolean {
  if (sizeValue === null || sizeUnit === null) {
    return false;
  }
  if (sizeUnit !== "oz" && sizeUnit !== "ml" && sizeUnit !== "l") {
    return false;
  }
  return /^\d+(\.\d+)?$/.test(normalizeLeadingDot(sizeValue)) && Number(sizeValue) > 0;
}

/** Set exactly when this product's own chip vocabulary includes the
 * container chip: "bottle"/"can" container_type with a resolvable size.
 * Exported so ui/src/main.ts's cost-field label (Section 5's own "the
 * label follows the selected chip") can name the right word without
 * re-deriving this same check. */
export function resolvedContainerChipLabel(
  container: ReceivingProductContainerInfo,
): string | null {
  if (container.containerType === "keg") {
    // KEGRECV1: a keg is counted in kegs once its keg size is set; the server reads the ounces from that size.
    return container.defaultKegSize !== undefined &&
      container.defaultKegSize !== null &&
      container.defaultKegSize !== ""
      ? "Kegs"
      : null;
  }
  if (container.containerType !== "bottle" && container.containerType !== "can") {
    return null;
  }
  if (!containerSizeResolves(container.sizeValue, container.sizeUnit)) {
    return null;
  }
  return CONTAINER_TYPE_CHIP_LABELS[container.containerType] ?? null;
}

export function receivingUnitOptionsForItemKind(
  unitPriceCents: number | null,
  container?: ReceivingProductContainerInfo,
): readonly ReceivingUnitOption[] {
  if (unitPriceCents !== null) {
    return PACKAGED_RECEIVING_UNITS;
  }
  const containerChipLabel = container !== undefined ? resolvedContainerChipLabel(container) : null;
  if (containerChipLabel !== null) {
    return [
      { value: "oz", label: "Ounces" },
      { value: CONTAINER_RECEIVING_UNIT_VALUE, label: containerChipLabel },
    ];
  }
  if (container?.containerType === "keg") {
    // KEGRECV1: a keg is never received as bottles; with no keg size set it can only be typed in ounces.
    return KEG_NO_SIZE_RECEIVING_UNITS;
  }
  return LIQUOR_RECEIVING_UNITS;
}

/** INV8-F1 Section 5's own "the existing generic cost field's own label
 * changes with the selected chip" rule: the received-cost field means
 * cents per ounce under "Ounces", cents per container under the new
 * container chip, and cents per unit under a packaged product's single
 * chip, never one blanket wording once a chip can mean two different
 * things. `selectedUnitValue` is the CHIP's own `value` (never the
 * resolved product unit), so an unselected picker (before any chip is
 * tapped) falls back to the same generic "unit" wording this field
 * always showed before this item, rather than guessing. */
export function receivingCostFieldLabels(
  selectedUnitValue: string | undefined,
  container: ReceivingProductContainerInfo | undefined,
  // Undefined for the outside-order screen's own single, not-per-line
  // field, which named no product in its aria-label before this item
  // either; present for the per-line receiving screen, matching this
  // item's own worked wording exactly ("Cost per bottle received for
  // {product}, in dollars").
  productName: string | undefined,
): { readonly placeholder: string; readonly ariaLabel: string } {
  const containerChipLabel = container !== undefined ? resolvedContainerChipLabel(container) : null;
  const word =
    selectedUnitValue === ORDER_UNIT_RECEIVING_UNIT_VALUE
      ? "bottle"
      : selectedUnitValue === CONTAINER_RECEIVING_UNIT_VALUE && containerChipLabel !== null
        ? containerChipLabel.replace(/s$/, "").toLowerCase()
        : selectedUnitValue === "oz"
        ? "ounce"
        : selectedUnitValue === "unit"
          ? "unit"
          : selectedUnitValue === "bottle"
            ? "bottle"
            : "unit";
  return {
    placeholder: `Cost per ${word} $`,
    ariaLabel:
      productName !== undefined
        ? `Cost per ${word} received for ${productName}, in dollars`
        : `Cost per ${word} received, in dollars`,
  };
}

// ---------------------------------------------------------------------
// PO6 Slice 5 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md,
// Decision 4 slice 5; docs/build-briefs/PO6-S5-corrections-and-
// exceptions.md). Correcting a posted receipt, and entering/resolving a
// purchase order's own `exception` state.
// ---------------------------------------------------------------------

const PURCHASE_EXCEPTION_REASON_LABELS: Readonly<Record<string, string>> = {
  short_delivery: "Short delivery",
  over_delivery: "Over delivery",
  damaged: "Damaged",
  wrong_item: "Wrong item",
  cost_mismatch: "Cost mismatch",
  vendor_cancelled: "Vendor cancelled",
};

/** Falls back to the raw value for a reason this build does not
 * recognize, the same "show it, don't swallow it" choice
 * purchaseOrderStatusLabel makes above. */
export function purchaseExceptionReasonLabel(reason: string): string {
  return PURCHASE_EXCEPTION_REASON_LABELS[reason] ?? reason;
}

const PURCHASE_EXCEPTION_RESOLUTION_LABELS: Readonly<Record<string, string>> = {
  delivery_completed: "Delivery completed",
  accepted_short: "Accept as short",
  awaiting_delivery: "Still awaiting delivery",
};

export function purchaseExceptionResolutionLabel(resolution: string): string {
  return PURCHASE_EXCEPTION_RESOLUTION_LABELS[resolution] ?? resolution;
}

export interface ExceptionResolutionOutcome {
  readonly legal: boolean;
  /** Set only when legal is true: the exact toStatus a submit must send,
   * never a value a person picks. */
  readonly targetStatus: string | null;
  /** Set only when legal is false: why this resolution does not apply to
   * the order's own current variance, in the same terms
   * src/domain/receiving.ts's resolvePurchaseOrderException itself
   * refuses with. */
  readonly illegalReason: string | null;
}

/** Mirrors src/domain/receiving.ts's own resolvePurchaseOrderException
 * EXACTLY -- verified against that function's real source before this
 * was written, not assumed. The target status a resolution computes to
 * is a function of the order's own derived variance (every line's
 * outstandingQuantity/receivedQuantity, from GET .../purchasing/orders/
 * :orderId/receiving), never a value a person picks: this function
 * computes it so the screen can show it BEFORE submit, and disable a
 * resolution whose own required variance shape does not hold, with the
 * same reason the server itself would refuse it with. */
export function computeExceptionResolutionTarget(
  resolution: string,
  lines: readonly Pick<LineReceivingStateDto, "outstandingQuantity" | "receivedQuantity">[],
): ExceptionResolutionOutcome {
  const everyLineZero = lines.every((line) => Number(line.outstandingQuantity) === 0);
  const anyReceived = lines.some((line) => Number(line.receivedQuantity) > 0);

  if (resolution === "delivery_completed") {
    return everyLineZero
      ? { legal: true, targetStatus: "received", illegalReason: null }
      : {
          legal: false,
          targetStatus: null,
          illegalReason:
            "requires the derived variance to be zero on every line; at least one line is still outstanding",
        };
  }
  if (resolution === "accepted_short") {
    return !everyLineZero
      ? { legal: true, targetStatus: "received", illegalReason: null }
      : {
          legal: false,
          targetStatus: null,
          illegalReason: "requires a nonzero variance; every line is already fully received",
        };
  }
  if (resolution === "awaiting_delivery") {
    return !everyLineZero
      ? {
          legal: true,
          targetStatus: anyReceived ? "partially_received" : "placed",
          illegalReason: null,
        }
      : {
          legal: false,
          targetStatus: null,
          illegalReason: "requires a nonzero variance; every line is already fully received",
        };
  }
  return { legal: false, targetStatus: null, illegalReason: `unknown resolution ${resolution}` };
}

// Mirrors src/domain/inventoryQuantity.ts's own parseQuantity/
// formatQuantity exactly (four decimal places, scaled BigInt
// arithmetic), reproduced here so a corrected quantity a person enters
// -- what the receipt SHOULD have said -- turns into the SIGNED DELTA
// the correction route actually accepts, without floating-point
// subtraction (ADR-003 Decision 3: "A correction is made by selecting
// the receipt line and stating what it should have been, never by
// typing a delta.") This function computes the delta from two absolute
// quantities; it never receives one a person typed AS a delta.
const RECEIPT_CORRECTION_QUANTITY_SCALE = 10000n;
const RECEIPT_CORRECTION_QUANTITY_PATTERN = /^-?\d+(\.\d{1,4})?$/;

function parseReceiptCorrectionQuantity(value: string, label: string): bigint {
  const trimmed = normalizeLeadingDot(value);
  if (!RECEIPT_CORRECTION_QUANTITY_PATTERN.test(trimmed)) {
    throw new Error(
      `${label} must be a decimal number with at most four decimal places (e.g. "12.5"); received "${value}"`,
    );
  }
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fractionalPartRaw] = unsigned.split(".");
  const fractionalPart = (fractionalPartRaw ?? "").padEnd(4, "0");
  const magnitude =
    BigInt(wholePart as string) * RECEIPT_CORRECTION_QUANTITY_SCALE + BigInt(fractionalPart);
  return negative ? -magnitude : magnitude;
}

function formatReceiptCorrectionQuantity(scaled: bigint): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const whole = magnitude / RECEIPT_CORRECTION_QUANTITY_SCALE;
  const fraction = magnitude % RECEIPT_CORRECTION_QUANTITY_SCALE;
  if (fraction === 0n) {
    return `${negative ? "-" : ""}${whole}`;
  }
  const fractionStr = fraction.toString().padStart(4, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}.${fractionStr}`;
}

/** original="6", corrected="4" -> "-2"; original="6", corrected="6" ->
 * "0"; original="4.5", corrected="5" -> "0.5". The one place a
 * correction's own signed delta is computed; every call site hands this
 * function two ABSOLUTE quantities (the receipt's own stored quantity,
 * and what a person entered as the corrected quantity), never a delta
 * typed directly. */
export function computeReceiptCorrectionQuantityDelta(
  originalQuantity: string,
  correctedQuantity: string,
): string {
  const original = parseReceiptCorrectionQuantity(originalQuantity, "the original quantity");
  const corrected = parseReceiptCorrectionQuantity(correctedQuantity, "the corrected quantity");
  return formatReceiptCorrectionQuantity(corrected - original);
}

/** Straight from the receipt's own unit_cost_cents; "No cost recorded"
 * when the receipt was never cost-bearing, never a misleading $0.00. */
export function formatReceiptUnitCost(
  receipt: Pick<InventoryReceiptDto, "unit_cost_cents">,
): string {
  return receipt.unit_cost_cents === null
    ? "No cost recorded"
    : formatCents(receipt.unit_cost_cents);
}

/** INVPOWALK1 (F7): one receipt row in words a person reads at a glance. A container receipt reads in what came in and
 * what each cost ("Received 2 bottles at $55.00 each"); a volume receipt names its price per unit ("Received 50.721 oz at
 * $2.17 per oz") instead of a bare "$2.17" that reads as a total. Any other receipt keeps the earlier wording. */
export function formatReceiptSummary(
  receipt: Pick<
    InventoryReceiptDto,
    | "quantity"
    | "unit"
    | "unit_cost_cents"
    | "received_container_type"
    | "received_container_count"
    | "received_cost_per_container_cents"
  >,
): string {
  const containerCount = receipt.received_container_count;
  const containerType = receipt.received_container_type;
  if (containerCount !== null && containerType !== null) {
    const count = formatReceivingQuantity(containerCount);
    const word = Number(containerCount) === 1 ? containerType : `${containerType}s`;
    const each =
      receipt.received_cost_per_container_cents === null
        ? ""
        : ` at ${formatCartMoney(receipt.received_cost_per_container_cents)} each`;
    return `Received ${count} ${word}${each}`;
  }
  const quantity = `Received ${formatReceivingQuantity(receipt.quantity)} ${receipt.unit}`;
  if (receipt.unit_cost_cents !== null && (receipt.unit === "oz" || receipt.unit === "ml")) {
    return `${quantity} at ${formatCartMoney(receipt.unit_cost_cents)} per ${receipt.unit}`;
  }
  return `${quantity} - ${formatReceiptUnitCost(receipt)}`;
}

// ---------------------------------------------------------------------
// PO6 Slice 6 (docs/adr/ADR-003-purchasing-and-receiving-surfaces.md,
// Decision 4 Slice 6; docs/build-briefs/PO6-S6-outside-system-orders.md).
// Recording an order placed outside the product: the "Recorded this
// visit" list's own two display fields.
// ---------------------------------------------------------------------

/** "3.0000"/"bottle" -> "3 bottle": the same NUMERIC(12,4)-string trim
 * formatReceivingQuantity already applies, paired with the unit -- both a
 * pass-through of POST .../inventory/receipts' own response
 * (RecordedOutsideSystemReceiptDto), never re-derived. */
export function formatOutsideSystemReceiptQuantity(receipt: {
  readonly quantity: string;
  readonly unit: string;
}): string {
  return `${formatReceivingQuantity(receipt.quantity)} ${receipt.unit}`;
}

/** The vendor's own order or confirmation number, exactly as recorded
 * (a receipt's own source_reference), or a plain-language fallback when
 * none was supplied. Whitespace-only counts as none: a reference field a
 * person left blank or only tapped into should read the same either
 * way. */
export function formatOutsideSystemReceiptReference(sourceReference: string | null): string {
  const trimmed = sourceReference?.trim() ?? "";
  return trimmed.length === 0 ? "No order number" : trimmed;
}

// ---------------------------------------------------------------------
// PO6-S2-F2 and PO8 (docs/build-briefs/PO8-cart-total-and-minimum.md,
// design/mockups/po-cart-total-minimum-notes.md): the cart's estimated
// totals, minimum order notes and order-by rows. Every figure and every
// status arrives from the server; these functions choose words only.
// ---------------------------------------------------------------------

/** formatCents with thousands grouped ("$40,945.88"), the mockup's form
 * for the big cart numbers. Groups the digits of the formatted string; no
 * arithmetic on money. */
export function formatCartMoney(cents: number): string {
  const plain = formatCents(cents);
  const negative = plain.startsWith("-");
  const body = negative ? plain.slice(1) : plain;
  const [dollarsPart, centsPart] = body.slice(1).split(".");
  const grouped = (dollarsPart ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}$${grouped}.${centsPart ?? "00"}`;
}

function capitalizeFirst(text: string): string {
  return `${text.slice(0, 1).toUpperCase()}${text.slice(1)}`;
}

export function cartLineUnitLabel(line: Pick<CartLineDto, "orderUnit">): string {
  return line.orderUnit === null ? "Unit" : capitalizeFirst(line.orderUnit.toLowerCase());
}

/** "$19.50 per bottle", the line's own inputs beside its total. */
export function formatCartLineUnitPrice(
  line: Pick<CartLineDto, "unitCostCents" | "orderUnit">,
): string {
  if (line.unitCostCents === null) {
    return "No price";
  }
  const unit = line.orderUnit === null ? "unit" : line.orderUnit.toLowerCase();
  return `${formatCartMoney(line.unitCostCents)} per ${unit}`;
}

export function formatCartLineTotal(line: Pick<CartLineDto, "lineEstimateCents">): string {
  return line.lineEstimateCents === null
    ? "No line total"
    : formatCartMoney(line.lineEstimateCents);
}

export function cartEstimateHeading(estimate: Pick<CartEstimateDto, "isComplete">): string {
  return estimate.isComplete ? "Estimated cart total" : "Estimated cart total so far";
}

export function cartEstimateValue(
  estimate: Pick<CartEstimateDto, "isComplete" | "totalCents">,
): string {
  if (estimate.isComplete) {
    return formatCartMoney(estimate.totalCents);
  }
  // Nothing priced at all: a floor of $0.00 would read as a real figure.
  return estimate.totalCents === 0
    ? "Not priced"
    : `At least ${formatCartMoney(estimate.totalCents)}`;
}

export function vendorEstimateHeading(
  summary: Pick<CartVendorSummaryDto, "totalIsComplete" | "pricedLineCount">,
): string {
  return summary.totalIsComplete || summary.pricedLineCount === 0
    ? "Estimated total"
    : "Estimated total so far";
}

export function vendorEstimateValue(
  summary: Pick<
    CartVendorSummaryDto,
    "totalIsComplete" | "pricedLineCount" | "estimatedTotalCents"
  >,
): string {
  if (summary.totalIsComplete) {
    return formatCartMoney(summary.estimatedTotalCents);
  }
  return summary.pricedLineCount === 0
    ? "Not priced"
    : `At least ${formatCartMoney(summary.estimatedTotalCents)}`;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function cartSummaryCounts(
  estimate: Pick<CartEstimateDto, "vendorCount" | "lineCount">,
): string {
  return `${plural(estimate.vendorCount, "vendor", "vendors")}, ${plural(estimate.lineCount, "line", "lines")}`;
}

/** One chip per class of problem, in the mockup's order. */
export function cartSummaryChips(
  estimate: Pick<
    CartEstimateDto,
    "underMinimumVendorCount" | "maybeUnderMinimumVendorCount" | "unpricedLineCount"
  >,
): string[] {
  const chips: string[] = [];
  const under = estimate.underMinimumVendorCount;
  if (under > 0) {
    chips.push(`${under} ${under === 1 ? "vendor is" : "vendors are"} under the minimum`);
  }
  const maybe = estimate.maybeUnderMinimumVendorCount;
  if (maybe > 0) {
    chips.push(`${maybe} ${maybe === 1 ? "vendor might" : "vendors might"} be under the minimum`);
  }
  const unpriced = estimate.unpricedLineCount;
  if (unpriced > 0) {
    chips.push(`${unpriced} ${unpriced === 1 ? "line has" : "lines have"} no price`);
  }
  return chips;
}

export type VendorMinimumDescription =
  | { kind: "none"; quietText: string }
  | {
      kind: "meter";
      meterText: string;
      fillPercent: number;
      noteTone: "warn" | "ok";
      noteText: string;
    };

/** Null for the no-vendor group (no minimum row at all). Warn, never
 * block: this only describes, nothing here gates the Place button. */
export function describeVendorMinimum(
  summary: CartVendorSummaryDto,
): VendorMinimumDescription | null {
  if (summary.vendorId === null) {
    return null;
  }
  if (summary.minimumOrderCents === null || summary.minimumStatus === "none") {
    return { kind: "none", quietText: "No minimum order on file." };
  }
  const minimum = formatCartMoney(summary.minimumOrderCents);
  const total = formatCartMoney(summary.estimatedTotalCents);
  const meterText = summary.totalIsComplete
    ? `${total} of ${minimum} minimum`
    : `${total} so far of ${minimum} minimum`;
  const fillPercent = summary.minimumFillPercent ?? 0;
  if (summary.minimumStatus === "under") {
    return {
      kind: "meter",
      meterText,
      fillPercent,
      noteTone: "warn",
      noteText: `${formatCartMoney(summary.shortfallCents ?? 0)} under this vendor's ${minimum} minimum. You can still place the order.`,
    };
  }
  if (summary.minimumStatus === "maybe_under") {
    const lines = summary.unpricedLineCount;
    return {
      kind: "meter",
      meterText,
      fillPercent,
      noteTone: "warn",
      noteText: `Might be under this vendor's ${minimum} minimum. ${lines} ${lines === 1 ? "line has" : "lines have"} no price yet.`,
    };
  }
  return {
    kind: "meter",
    meterText,
    fillPercent,
    noteTone: "ok",
    noteText: `Meets this vendor's ${minimum} minimum.`,
  };
}

function joinDays(days: readonly string[]): string {
  if (days.length <= 1) {
    return days.join("");
  }
  return `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}`;
}

export interface VendorScheduleDescription {
  text: string;
  tone: "neutral" | "warn";
  /** The row lacks delivery days or a cutoff, so it carries an
   * "Add delivery info" button. */
  needsDeliveryInfo: boolean;
}

/** Null for the no-vendor group (no schedule row). */
export function describeVendorSchedule(
  summary: CartVendorSummaryDto,
): VendorScheduleDescription | null {
  if (summary.vendorId === null) {
    return null;
  }
  const schedule = summary.schedule;
  const time = schedule.orderByTime ?? "";
  switch (schedule.kind) {
    case "open":
      return {
        text: `Order by ${time} ${schedule.orderByDay ?? ""} for ${schedule.deliveryDay ?? ""} delivery.`,
        tone: "neutral",
        needsDeliveryInfo: false,
      };
    case "past_cutoff":
      return {
        text: `Today's ${time} cutoff has passed. An order placed now arrives ${schedule.deliveryDay ?? ""}.`,
        tone: "warn",
        needsDeliveryInfo: false,
      };
    case "days_only":
      return {
        text: `Delivers ${joinDays(schedule.deliveryDays)}. No order cutoff on file.`,
        tone: "neutral",
        needsDeliveryInfo: true,
      };
    case "cutoff_only":
      return {
        text: `Order by ${time} today. No delivery days on file.`,
        tone: "neutral",
        needsDeliveryInfo: true,
      };
    case "past_cutoff_only":
      return {
        text: `Today's ${time} cutoff has passed. No delivery days on file.`,
        tone: "warn",
        needsDeliveryInfo: true,
      };
    case "none":
      return {
        text: "No delivery days or order cutoff on file.",
        tone: "neutral",
        needsDeliveryInfo: true,
      };
  }
}

// ---------------------------------------------------------------------
// CORE9 (docs/build-briefs/CORE8-10-count-receive-additem.md, section 2.2; core walkthrough 2026-10-10, item 10).
// ---------------------------------------------------------------------

export interface ReceivingLinePrefill {
  /** The remaining quantity in the line's order unit, or "" when nothing is left. */
  readonly quantity: string;
  /** The chip the order's own unit names, or undefined when no chip is certain (the person still picks it). */
  readonly unitValue: string | undefined;
  /** Dollars in the cost field's own meaning for that chip ("20.00"), or undefined (the field stays blank). */
  readonly costDollars: string | undefined;
}

function centsToDollarsText(cents: number): string {
  return (Math.round(cents) / 100).toFixed(2);
}

/** What Receive against an order starts each open line with: the remaining quantity, and the order's unit cost.
 * A chip deciding a business fact is never guessed: a case line has one chip (its order unit) and a packaged line one
 * (Units); a bottle or can order unit selects the matching Bottles or Cans chip; anything else (a keg, an unknown
 * unit) keeps the quantity but selects no chip and leaves cost blank, because the cost means something different
 * under each chip. A case line's cost is per bottle (the case cost over the bottles in it), the field's own meaning. */
export function receivingLinePrefill(
  line: Pick<
    LineReceivingStateDto,
    "outstandingQuantity" | "orderUnit" | "bottlesPerOrderUnit"
  >,
  unitCostCentsAtPlacement: number | null | undefined,
  unitOptions: readonly ReceivingUnitOption[],
): ReceivingLinePrefill {
  const remaining = Number(line.outstandingQuantity);
  const quantity = Number.isFinite(remaining) && remaining > 0 ? formatReceivingQuantity(line.outstandingQuantity) : "";
  const hasCost =
    unitCostCentsAtPlacement !== null &&
    unitCostCentsAtPlacement !== undefined &&
    Number.isFinite(unitCostCentsAtPlacement) &&
    unitCostCentsAtPlacement >= 0;
  const has = (value: string): boolean => unitOptions.some((option) => option.value === value);
  const pack = Number(line.bottlesPerOrderUnit);

  if (receivingLineIsPacked(line)) {
    const costDollars = hasCost
      ? centsToDollarsText((unitCostCentsAtPlacement as number) / pack)
      : undefined;
    return {
      quantity,
      unitValue: has(ORDER_UNIT_RECEIVING_UNIT_VALUE) ? ORDER_UNIT_RECEIVING_UNIT_VALUE : undefined,
      costDollars,
    };
  }

  const singleUnit = pack === 1;
  const orderUnit = (line.orderUnit ?? "").trim().toLowerCase();
  let unitValue: string | undefined;
  if (singleUnit) {
    if (orderUnit === "bottle" || orderUnit === "can") {
      unitValue = has(CONTAINER_RECEIVING_UNIT_VALUE)
        ? CONTAINER_RECEIVING_UNIT_VALUE
        : orderUnit === "bottle" && has("bottle")
          ? "bottle"
          : undefined;
    } else if (orderUnit === "keg") {
      // KEGRECV1: a keg line starts on its own unit, at the order's cost per keg.
      unitValue = has(CONTAINER_RECEIVING_UNIT_VALUE) ? CONTAINER_RECEIVING_UNIT_VALUE : undefined;
    } else if (orderUnit === "unit" && has("unit")) {
      unitValue = "unit";
    }
  }
  if (unitValue === undefined && unitOptions.length === 1 && singleUnit) {
    unitValue = unitOptions[0]?.value;
  }
  return {
    quantity,
    unitValue,
    costDollars:
      unitValue !== undefined && hasCost
        ? centsToDollarsText(unitCostCentsAtPlacement as number)
        : undefined,
  };
}

const VENDOR_MISSING_LABELS: Readonly<Record<string, string>> = {
  vendor_inactive: "Vendor is inactive",
  missing_contact: "Contact",
  missing_package: "Order unit or pack size on an offer",
  missing_cost: "Cost on an offer",
};
const VENDOR_LEVEL_MISSING: ReadonlySet<string> = new Set(["vendor_inactive", "missing_contact"]);

export interface VendorMissingList {
  /** Plain labels, in the order a manager fixes them. Empty when nothing is missing. */
  readonly items: readonly string[];
  /** Where the one fix link goes: the vendor's row in Settings (any vendor-level item, or delivery info), or the first
   * affected item's editor when only offers are incomplete. */
  readonly fixTarget: "vendor" | "offer" | null;
}

/** CORE9: every blocker a vendor group carries, once, as one list with one place to fix it. `blockers` may repeat
 * (one per line); `needsDeliveryInfo` comes from describeVendorSchedule. Order: vendor, contact, delivery, offers. */
export function describeVendorMissingList(
  blockers: readonly string[],
  needsDeliveryInfo: boolean,
): VendorMissingList {
  const present = new Set(blockers);
  const items: string[] = [];
  for (const key of ["vendor_inactive", "missing_contact"]) {
    if (present.has(key)) items.push(VENDOR_MISSING_LABELS[key] as string);
  }
  if (needsDeliveryInfo) items.push("Delivery days or order cutoff");
  for (const key of ["missing_package", "missing_cost"]) {
    if (present.has(key)) items.push(VENDOR_MISSING_LABELS[key] as string);
  }
  const vendorLevel = needsDeliveryInfo || [...present].some((key) => VENDOR_LEVEL_MISSING.has(key));
  const offerLevel = present.has("missing_package") || present.has("missing_cost");
  return {
    items,
    fixTarget: vendorLevel ? "vendor" : offerLevel ? "offer" : null,
  };
}
